import { useState, useCallback } from 'react';
import { chatService, ChatMessage, ChatConversation } from '../services/chatService';
import { authService } from '../services/authService';

export function useChat() {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConversations = useCallback(async () => {
    const token = authService.getToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await chatService.getConversations(token);
      setConversations(data);
    } catch (e) {
      setError('Failed to load conversations.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async (conversationId: string) => {
    const token = authService.getToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await chatService.getHistory(token, conversationId);
      setMessages(data.messages);
      setCurrentConversationId(conversationId);
    } catch (e) {
      setError('Failed to load conversation history.');
    } finally {
      setLoading(false);
    }
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    const token = authService.getToken();
    if (!text.trim()) return;
    if (!token) {
      setError('Sign in to chat with Nereus.');
      return;
    }

    // Optimistic user message
    const optimisticUser: ChatMessage = {
      conversationId: currentConversationId ?? '',
      messageId: `optimistic-${Date.now()}`,
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
      cacheHit: false,
    };
    setMessages(prev => [...prev, optimisticUser]);
    setSending(true);
    setError(null);

    try {
      const resp = await chatService.sendMessage(token, text, currentConversationId);
      setCurrentConversationId(resp.conversationId);
      // Replace optimistic message with confirmed messages
      setMessages(prev => [
        ...prev.filter(m => m.messageId !== optimisticUser.messageId),
        resp.userMessage,
        resp.aiMessage,
      ]);
      // Update conversation list
      setConversations(prev => {
        const exists = prev.find(c => c.conversationId === resp.conversationId);
        if (!exists) {
          const newConv: ChatConversation = {
            userId: authService.getUser()?.userId ?? '',
            conversationId: resp.conversationId,
            title: text.length > 50 ? text.slice(0, 50) + '…' : text,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            messageCount: 2,
          };
          return [newConv, ...prev];
        }
        return prev.map(c =>
          c.conversationId === resp.conversationId
            ? { ...c, updatedAt: new Date().toISOString(), messageCount: c.messageCount + 2 }
            : c
        );
      });
    } catch (e: any) {
      setMessages(prev => prev.filter(m => m.messageId !== optimisticUser.messageId));
      setError(e?.message ?? 'Failed to send message.');
    } finally {
      setSending(false);
    }
  }, [currentConversationId]);

  const startNewConversation = useCallback(() => {
    setCurrentConversationId(undefined);
    setMessages([]);
    setError(null);
  }, []);

  return {
    conversations,
    messages,
    currentConversationId,
    loading,
    sending,
    error,
    loadConversations,
    loadHistory,
    sendMessage,
    startNewConversation,
  };
}
